export const useEntryStore = defineStore('entryStore', {
    state: () => ({
        entry: {},
        seomatic: {},
        variables: {},
        seoHeadScripts: [],
        seoBodyScripts: [],
        seoBodyNoscripts: [],
    }),
    actions: {
        async loadEntry(uri) {
            const { cmsQuery } = useGraphQL()

            const {data: pageData } = await cmsQuery({
                uri: `page-data/page/${uri}`,
                key: `entry-${uri}`,
            })

            const redirect = get(pageData.value?.data, 'retourResolveRedirect', {})

            if (redirect && !isEmpty(redirect.redirectDestUrl) && redirect.enabled) {
                const route = useRoute()
                return navigateTo({
                    path: redirect.redirectDestUrl,
                    query: route.query
                }, {
                    redirectCode: redirect.redirectHttpCode || 301,
                    replace: true,
                    external: true
                })
            }

            // replace page data entry
            this.entry = get(pageData.value?.data, 'entry', {})

            if (isEmpty(this.entry?.slug)) {
                throw createError({
                    statusMessage: 'Page not found',
                    message: 'The page you are looking for does not exist. <br /><br />Try finding what you are looking for by searching from the top menu or return back to the homepage',
                    statusCode: 404,
                })
            }

            // Handle RedirectPage entries
            if (this.entry?.__typename === 'ds_RedirectPage_Entry') {
                const redirectUrl = this.entry?.redirectLink?.[0].linkUrl

                if (redirectUrl) {
                    return navigateTo({
                        path: redirectUrl,
                        query: useRoute().query
                    }, {
                        redirectCode: 301,
                        replace: true,
                        external: true
                    })
                }
            }

            this.initEntry()
        },

        async loadArticle(type: string, slug: string) {
            const { cmsQuery } = useGraphQL()

            const {data: articleData } = await cmsQuery({
                uri: `page-data/article/${type}/${slug}`,
                key: `entry-${type}-${slug}`,
            })


            // replace page data entry
            this.entry = get(articleData.value?.data, 'entry', {})

            if (isEmpty(this.entry?.slug)) {
                throw createError({
                    statusMessage: 'Page not found',
                    message: 'The page you are looking for does not exist. <br /><br />Try finding what you are looking for by searching from the top menu or return back to the homepage',
                    statusCode: 404,
                })
            }

            this.initEntry()

        },

        initEntry() {
            const micrositeStore = useMicrositeStore()

            this.seomatic = get(this.entry, 'seomatic', {})
            this.variables = JSON.parse(get(this.seomatic, 'metaSiteVarsContainer', '{}'))

            const scripts = Object.values(JSON.parse(get(this.seomatic, 'metaScriptContainer', '{}')))
            
            // Separate head and body scripts
            this.seoHeadScripts = scripts
                .filter(script => script.script)
                .map(script => ({
                    innerHTML: script.script,
                    type: 'text/javascript'
                }))

            // Parse body scripts from HTML strings, extracting script tags and their attributes
            this.seoBodyScripts = scripts
                .filter(script => script.bodyScript)
                .flatMap(script => 
                    Array.from(script.bodyScript.matchAll(/<script([^>]*)>(.*?)<\/script>/gs), match => {
                        const [, attributes, content] = match
                        const srcMatch = attributes.match(/src=["']([^"']+)["']/)
                        const typeMatch = attributes.match(/type=["']([^"']+)["']/)
                        
                        return {
                            tagPosition: 'bodyClose',
                            ...(srcMatch && { src: srcMatch[1] }),
                            ...(attributes.includes('async') && { async: true }),
                            ...(attributes.includes('defer') && { defer: true }),
                            ...(typeMatch && { type: typeMatch[1] }),
                            ...(content.trim() && { innerHTML: content.trim() })
                        }
                    })
                )

            // Extract noscript tags from body scripts
            this.seoBodyNoscripts = scripts
                .filter(script => script.bodyScript)
                .flatMap(script => 
                    Array.from(script.bodyScript.matchAll(/<noscript>(.*?)<\/noscript>/gs), match => ({
                        innerHTML: match[1].trim(),
                        tagPosition: 'bodyClose'
                    }))
                )

            // check if microsite
            if(['ds_MicrositeHomepage_Entry', 'ds_MicrositePage_Entry'].includes(this.entry.__typename)) {
                micrositeStore.initMicrosite(this.entry)
            }
        },


    },
})